<template>
  <div class="page-container">
    <div class="records-form">
      <h2 class="page-title">文章发布记录</h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <el-input v-model="searchForm.title" placeholder="搜索文章标题" clearable size="large"
          style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <!-- 添加分类下拉框 -->
        <el-select v-model="searchForm.category_id" placeholder="选择分类" clearable size="large"
          style="width: 180px; margin-right: 15px">
          <el-option label="不选择" :value="null" />
          <el-option v-for="item in categoryOptions" :key="item.id" :label="item.article_category" :value="item.id" />
        </el-select>

        <!-- 添加百度文库下拉框 -->
        <el-select v-model="searchForm.gzh_id" placeholder="选择百度文库" clearable size="large"
          style="width: 180px; margin-right: 15px">
          <el-option label="不选择" :value="null" />
          <el-option v-for="item in gzhOptions" :key="item.id" :label="item.ghz_name" :value="item.id" />
        </el-select>

        <el-button type="primary" size="large" @click="handleSearch">
          <el-icon>
            <Search />
          </el-icon>
          搜索
        </el-button>

        <el-button type="info" size="large" @click="resetSearch" style="margin-left: 10px">
          <el-icon>
            <Refresh />
          </el-icon>
          重置
        </el-button>
      </div>

      <!-- 记录列表 -->
      <el-table :data="recordList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
        <el-table-column label="百度文库" prop="gzh_name" width="180" />
        <el-table-column label="文章分类" prop="category_name" width="180" />
        <el-table-column label="文章标题" prop="title" min-width="220" show-overflow-tooltip />
        <el-table-column label="任务名称" prop="draft_task_name" width="150" />

        <!-- 新增错误提示列 -->
        <el-table-column label="错误提示" width="180">
          <template #default="{ row }">
            <el-popover v-if="row.error_message" placement="top-start" width="300" trigger="hover">
              <template #reference>
                <el-tag type="danger" size="small" style="cursor: pointer">
                  <el-icon>
                    <Warning />
                  </el-icon>
                  查看错误
                </el-tag>
              </template>
              <div class="error-message-content">
                {{ row.error_message }}
              </div>
            </el-popover>
            <span v-else class="empty-value">无错误</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="120">
          <template #default="{ row }">
            <el-tag :type="getStatusTagType(row.status)" size="small">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="草稿时间" prop="drafted_at" width="180" />
        <el-table-column label="发布时间" prop="published_at" width="180" />
        <el-table-column label="备注" prop="notes" width="180" show-overflow-tooltip />

        <el-table-column label="重试" width="100" fixed="right">
          <template #default="{ row }">
            <el-button type="warning" size="small" :disabled="[1, 2, 4, 5].includes(row.status)"
              @click="handleRetry(row)">
              <el-icon>
                <Refresh />
              </el-icon>
              重试
            </el-button>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="120" fixed="right">
          <template #default="scope">
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)"
              :disabled="scope.row.status === 2">
              <el-icon>
                <Delete />
              </el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchRecordList" @current-change="fetchRecordList" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { Search, Refresh, Delete, Warning } from '@element-plus/icons-vue';


export default {
  name: 'ZhihuRecord',
  components: { Search, Refresh, Delete, Warning },
  data() {
    return {
      loading: false,
      searchForm: {
        title: '',
        category_id: null,
        gzh_id: null
      },
      recordList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      categoryOptions: [],
      gzhOptions: []
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    }
  },
  async mounted() {
    await this.fetchGzhOptions();  // 先加载百度文库列表
    await this.fetchCategoryOptions();  // 再加载分类列表
    await this.fetchRecordList();  // 最后加载记录
  },
  methods: {

    async handleRetry(row) {
      // 前端先检查不允许重试的状态
      const disallowedStatuses = [1, 2, 4, 5]; // 待审核、正常、禁用状态不能重试
      if (disallowedStatuses.includes(row.status)) {
        this.$message.warning(`当前状态为"${this.getStatusText(row.status)}"，不能重试`);
        return;
      }

      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/wenku_article_record_management.php`,
          {
            action: 'retry',
            id: row.id,
            store_id: this.userInfo.id
          }
        );

        if (data.success) {
          this.$message.success('任务已重置为待发送状态');
          this.fetchRecordList(); // 刷新列表
        } else {
          this.$message.error(data.error || '重试任务失败');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '重试任务失败';
        this.$message.error(`重试失败: ${errorMsg}`);
        console.error('重试错误:', error);
      }
    },
    // 获取文章分类选项
    async fetchCategoryOptions() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/article_category_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.categoryOptions = data.data;
        }
      } catch (error) {
        console.error('获取文章分类失败:', error);
      }
    },

    // 获取百度文库选项
    async fetchGzhOptions() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/gzh_auth_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.gzhOptions = data.data.filter(item => item.field_type === 5);
        }
      } catch (error) {
        console.error('获取百度文库列表失败:', error);
      }
    },

    // 获取记录列表
    async fetchRecordList() {
      this.loading = true;
      try {
        const params = {
          action: 'search', // 统一使用search动作
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        if (this.searchForm.title) {
          params.title = this.searchForm.title;
        }

        if (this.searchForm.category_id) {
          params.article_category_id = this.searchForm.category_id;
        }

        if (this.searchForm.gzh_id) {
          params.gzh_id = this.searchForm.gzh_id;
        }
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/wenku_article_record_management.php`,
          params
        );

        if (data.data) {
          // 添加分类名称和百度文库名称
          this.recordList = data.data.map(item => ({
            ...item,
            category_name: this.getCategoryName(item.article_category_id),
            gzh_name: this.getGzhName(item.gzh_id)
          }));
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        this.$message.error('获取记录失败');
        console.error('获取记录错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 获取分类名称
    getCategoryName(id) {
      const category = this.categoryOptions.find(cat => cat.id == id);
      return category ? category.article_category : `分类${id}`;
    },

    // 获取百度文库名称
    getGzhName(id) {
      const gzh = this.gzhOptions.find(g => g.id == id);
      return gzh ? gzh.ghz_name : `百度文库${id}`;
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        1: '待发送',
        2: '发送成功',
        3: '发送失败',
        4: '发布处理中',
        5: '已提交发布'
      };
      return statusMap[status] || `状态${status}`;
    },

    // 获取状态标签类型
    getStatusTagType(status) {
      const typeMap = {
        1: 'warning',   // 待发送：黄色
        2: 'success',   // 成功：绿色
        3: 'danger',    // 失败：红色
        4: 'info',      // 处理中：蓝色
        5: 'primary'    // 已提交：深蓝色
      };
      return typeMap[status] || 'info';
    },

    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchRecordList();
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        title: '',
        category_id: null,
        gzh_id: null
      };
      this.pagination.page = 1;
      this.fetchRecordList();
    },

    // 删除记录
    async handleDelete(id) {
      this.$confirm('确定要删除此记录吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/wenku_article_record_management.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.id
            }
          );

          if (data.success) {
            this.$message.success('删除成功');
            this.fetchRecordList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }).catch(() => { });
    }
  }
}
</script>

<style scoped lang="scss">
.page-container {
  padding: 20px;
}

.records-form {
  max-width: 95%;
  margin: 0 auto;
  padding: 32px 40px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
  font-size: 24px;
  color: #1f2937;
  margin-bottom: 32px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.5px;
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

/* 表格样式优化 */
.el-table {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;

  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
  }

  :deep(.el-table__body) tr:hover>td {
    background-color: #f0f7ff !important;
  }
}

/* 操作按钮样式 */
.el-button {
  display: inline-flex;
  align-items: center;

  .el-icon {
    margin-right: 5px;
  }
}

/* 新增错误提示样式 */
.error-message-content {
  max-height: 200px;
  overflow-y: auto;
  word-break: break-word;
  white-space: pre-wrap;
  padding: 8px;
  font-size: 13px;
  line-height: 1.5;
}

.empty-value {
  color: #909399;
  font-style: italic;
  font-size: 12px;
}
</style>